import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link2, Copy, Check, AlertTriangle, UserPlus, PoundSterling, Clock, Wallet, Users, Banknote, ExternalLink } from 'lucide-react';
import { toast } from '../../common/utils/toast';
import { REFERRAL_LIST_LIMIT } from '../../config/affiliateConstants';

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

const TABS = [
    { id: 'overview', label: 'Overview', shortLabel: 'Link & stats', icon: Link2, accent: 'indigo' },
    { id: 'earnings', label: 'Earnings', shortLabel: 'Commission history', icon: Banknote, accent: 'amber' },
    { id: 'referrals', label: 'Referrals', shortLabel: 'Who signed up', icon: Users, accent: 'violet' }
];

const TAB_ACCENT = {
    indigo: {
        activeIcon: 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-200',
        activeTab: 'text-indigo-700 border-indigo-500 bg-indigo-50/60',
        badge: 'bg-indigo-600 text-white'
    },
    amber: {
        activeIcon: 'bg-amber-100 text-amber-700 ring-2 ring-amber-200',
        activeTab: 'text-amber-800 border-amber-500 bg-amber-50/60',
        badge: 'bg-amber-600 text-white'
    },
    violet: {
        activeIcon: 'bg-violet-100 text-violet-600 ring-2 ring-violet-200',
        activeTab: 'text-violet-700 border-violet-500 bg-violet-50/60',
        badge: 'bg-violet-600 text-white'
    }
};

const formatMoney = (amount, currency = 'GBP') => {
    const value = Number(amount || 0).toFixed(2);
    if (currency === 'GBP') return `£${value}`;
    return `${currency} ${value}`;
};

export default function AffiliatePage() {
    const [activeTab, setActiveTab] = useState('overview');
    const [data, setData] = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [referralsTotal, setReferralsTotal] = useState(0);
    const [referralPage, setReferralPage] = useState(1);
    const [loadingReferrals, setLoadingReferrals] = useState(false);
    const [commissions, setCommissions] = useState([]);
    const [commissionsTotal, setCommissionsTotal] = useState(0);
    const [commissionPage, setCommissionPage] = useState(1);
    const [loadingCommissions, setLoadingCommissions] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [tabsLoaded, setTabsLoaded] = useState({ earnings: false, referrals: false });

    useEffect(() => {
        loadAffiliate();
    }, []);

    useEffect(() => {
        if (loading || !data?.isAffiliate || data.status !== 'active') return;
        loadActiveTabData(activeTab);
    }, [activeTab, loading, data?.isAffiliate, data?.status]);

    const loadReferrals = async (page = 1) => {
        setLoadingReferrals(true);
        try {
            const offset = (page - 1) * REFERRAL_LIST_LIMIT;
            const refRes = await axios.get(`${API_URL}/affiliate/referrals`, {
                params: { limit: REFERRAL_LIST_LIMIT, offset }
            });
            setReferrals(refRes.data.referrals || []);
            setReferralsTotal(refRes.data.total ?? 0);
            setReferralPage(page);
        } catch (error) {
            console.error('Failed to load referrals', error);
        } finally {
            setLoadingReferrals(false);
        }
    };

    const loadCommissions = async (page = 1) => {
        setLoadingCommissions(true);
        try {
            const offset = (page - 1) * REFERRAL_LIST_LIMIT;
            const res = await axios.get(`${API_URL}/affiliate/commissions`, {
                params: { limit: REFERRAL_LIST_LIMIT, offset }
            });
            setCommissions(res.data.commissions || []);
            setCommissionsTotal(res.data.total ?? 0);
            setCommissionPage(page);
        } catch (error) {
            console.error('Failed to load commissions', error);
        } finally {
            setLoadingCommissions(false);
        }
    };

    const loadActiveTabData = async (tab) => {
        if (tab === 'earnings' && !tabsLoaded.earnings) {
            await loadCommissions(1);
            setTabsLoaded((prev) => ({ ...prev, earnings: true }));
        } else if (tab === 'referrals' && !tabsLoaded.referrals) {
            await loadReferrals(1);
            setTabsLoaded((prev) => ({ ...prev, referrals: true }));
        }
    };

    const loadAffiliate = async () => {
        try {
            const res = await axios.get(`${API_URL}/affiliate/me`);
            setData(res.data);
            if (!res.data?.isAffiliate || res.data.status !== 'active') {
                setReferrals([]);
                setReferralsTotal(0);
                setReferralPage(1);
                setCommissions([]);
                setCommissionsTotal(0);
                setCommissionPage(1);
                setTabsLoaded({ earnings: false, referrals: false });
            }
        } catch (error) {
            console.error('Failed to load affiliate status', error);
            toast.error('Failed to load affiliate info');
        } finally {
            setLoading(false);
        }
    };

    const handleBecomeAffiliate = async () => {
        setActivating(true);
        try {
            const res = await axios.post(`${API_URL}/affiliate/activate`);
            if (res.data.success) {
                setData(res.data);
                toast.success(res.data.message || 'You are now an affiliate!');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to activate affiliate');
        } finally {
            setActivating(false);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const copyLink = () => {
        if (!data?.referral_link) return;
        navigator.clipboard.writeText(data.referral_link);
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    const getTabBadge = (tabId) => {
        if (tabId === 'earnings' && (data?.commission_count > 0 || commissionsTotal > 0)) {
            return commissionsTotal || data?.commission_count || 0;
        }
        if (tabId === 'referrals' && (data?.total_referrals > 0 || referralsTotal > 0)) {
            return referralsTotal || data?.total_referrals || 0;
        }
        return null;
    };

    const totalReferralPages = Math.max(1, Math.ceil(referralsTotal / REFERRAL_LIST_LIMIT));
    const referralRangeStart = referralsTotal === 0 ? 0 : (referralPage - 1) * REFERRAL_LIST_LIMIT + 1;
    const referralRangeEnd = Math.min(referralPage * REFERRAL_LIST_LIMIT, referralsTotal);
    const totalCommissionPages = Math.max(1, Math.ceil(commissionsTotal / REFERRAL_LIST_LIMIT));
    const commissionRangeStart = commissionsTotal === 0 ? 0 : (commissionPage - 1) * REFERRAL_LIST_LIMIT + 1;
    const commissionRangeEnd = Math.min(commissionPage * REFERRAL_LIST_LIMIT, commissionsTotal);
    const currency = data?.currency || 'GBP';

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12 text-slate-500">
                Loading affiliate program...
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-5 animate-fade-in-up">
            <div>
                <h2 className="text-2xl font-bold text-slate-800">Affiliate Program</h2>
                <p className="text-slate-500 mt-1 text-sm">
                    Refer new studios and artists to bookink.uk and earn commission on their subscription payments.
                </p>
            </div>

            {!data?.isAffiliate && (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
                    <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <UserPlus size={28} className="text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Become an Affiliate</h3>
                    <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">
                        Get your unique referral link instantly. Share it with studios and artists. Earn recurring commission when they subscribe on bookink.uk.
                    </p>
                    <button
                        onClick={handleBecomeAffiliate}
                        disabled={activating}
                        className="bg-[#5C5CFF] hover:bg-[#4b4be6] disabled:opacity-50 text-white font-bold px-8 py-3 rounded-lg shadow-lg shadow-indigo-200 transition"
                    >
                        {activating ? 'Generating link...' : 'Become an Affiliate'}
                    </button>
                </div>
            )}

            {data?.isAffiliate && data.status === 'suspended' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                    <AlertTriangle size={40} className="text-red-500 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-red-800 mb-1">Suspended</h3>
                    <p className="text-red-600/70 text-xs">Contact admin</p>
                </div>
            )}

            {data?.isAffiliate && data.status === 'active' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white px-2 sm:px-4 pt-3 pb-0">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            {TABS.map(({ id, label, shortLabel, icon: Icon, accent }) => {
                                const badge = getTabBadge(id);
                                const isActive = activeTab === id;
                                const colors = TAB_ACCENT[accent];
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => handleTabChange(id)}
                                        className={`group flex items-center gap-3 min-w-[140px] sm:min-w-[160px] px-4 py-3.5 rounded-t-xl border-b-2 transition-all whitespace-nowrap ${
                                            isActive
                                                ? `${colors.activeTab} border-b-2 shadow-sm`
                                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-white/70'
                                        }`}
                                    >
                                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all ${
                                            isActive
                                                ? colors.activeIcon
                                                : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-600'
                                        }`}>
                                            <Icon size={18} strokeWidth={isActive ? 2.25 : 2} />
                                        </span>
                                        <span className="text-left min-w-0">
                                            <span className="flex items-center gap-2">
                                                <span className={`block text-sm font-bold leading-tight ${isActive ? '' : 'text-slate-700'}`}>
                                                    {label}
                                                </span>
                                                {badge != null && (
                                                    <span className={`text-[10px] font-bold min-w-[1.25rem] h-5 px-1.5 rounded-full flex items-center justify-center ${
                                                        isActive ? colors.badge : 'bg-slate-200 text-slate-600'
                                                    }`}>
                                                        {badge}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={`hidden sm:block text-[11px] mt-0.5 leading-tight ${
                                                isActive ? 'text-slate-500' : 'text-slate-400'
                                            }`}>
                                                {shortLabel}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-5 sm:p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-5 animate-fade-in-up">
                            {!data.auto_payout_ready && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                                    <AlertTriangle size={22} className="text-amber-600 shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-amber-900">Bank details required for automatic payouts</p>
                                        <p className="text-xs text-amber-800/80 mt-1 leading-relaxed">
                                            Commissions stay pending until you add your bank account on bookink.uk.
                                            After you save bank details there, payouts will be sent automatically when you earn commission.
                                        </p>
                                        <a
                                            href={data.bank_setup_url || 'https://server.bookink.uk/studio-user/bank-account'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-amber-900 underline hover:text-amber-950"
                                        >
                                            Add bank details on server.bookink.uk
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                    <Link2 size={20} className="text-green-600" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-800">Your Referral Link</h3>
                                    <p className="text-xs text-slate-400">
                                        Code: <span className="font-mono text-indigo-600">{data.referral_code}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    value={data.referral_link || ''}
                                    className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 font-mono truncate"
                                />
                                <button
                                    onClick={copyLink}
                                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-3 rounded-lg font-bold text-sm shrink-0"
                                >
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>

                            <p className="text-xs text-slate-400 leading-relaxed">
                                Share this link with studios and artists. When they sign up on bookink.uk and subscribe, you earn commission on each payment.
                            </p>

                            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                                <div className="rounded-xl p-4 bg-gradient-to-br from-slate-50 to-white border border-slate-100">
                                    <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Total referrals</p>
                                    <p className="text-2xl font-bold text-slate-800 mt-1">{data.total_referrals ?? 0}</p>
                                </div>
                                <div className="rounded-xl p-4 bg-gradient-to-br from-indigo-50/50 to-white border border-indigo-100">
                                    <p className="text-xs text-indigo-600 uppercase tracking-wide font-semibold flex items-center gap-1">
                                        <PoundSterling size={12} /> Lifetime earnings
                                    </p>
                                    <p className="text-2xl font-bold text-slate-800 mt-1">
                                        {formatMoney(data.lifetime_earnings, currency)}
                                    </p>
                                </div>
                                <div className="rounded-xl p-4 bg-gradient-to-br from-amber-50 to-white border border-amber-100">
                                    <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold flex items-center gap-1">
                                        <Clock size={12} /> Pending payout
                                    </p>
                                    <p className="text-2xl font-bold text-amber-900 mt-1">
                                        {formatMoney(data.pending_earnings, currency)}
                                    </p>
                                </div>
                                <div className="rounded-xl p-4 bg-gradient-to-br from-green-50 to-white border border-green-100">
                                    <p className="text-xs text-green-700 uppercase tracking-wide font-semibold flex items-center gap-1">
                                        <Wallet size={12} /> Paid out
                                    </p>
                                    <p className="text-2xl font-bold text-green-900 mt-1">
                                        {formatMoney(data.paid_earnings, currency)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'earnings' && (
                        <div className="animate-fade-in-up">
                            <div className="mb-4">
                                <h3 className="text-base font-bold text-slate-800">
                                    Commission history
                                </h3>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    {commissionsTotal} payment{commissionsTotal === 1 ? '' : 's'} — earned when referred studios subscribe
                                </p>
                            </div>

                            {loadingCommissions && commissions.length === 0 ? (
                                <p className="py-8 text-center text-sm text-slate-500">Loading commissions...</p>
                            ) : commissionsTotal === 0 ? (
                                <div className="py-10 text-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
                                    <Banknote size={32} className="mx-auto text-slate-300 mb-2" />
                                    <p className="text-sm font-medium text-slate-600">No commissions yet</p>
                                    <p className="text-xs text-slate-400 mt-1">Earnings appear when a referred user pays.</p>
                                </div>
                            ) : (
                                <div className={`rounded-xl border border-slate-200 overflow-hidden ${loadingCommissions ? 'opacity-50' : ''}`}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead>
                                                <tr className="text-slate-500 border-b border-slate-200 bg-slate-50">
                                                    <th className="px-4 py-3 font-semibold w-12">#</th>
                                                    <th className="px-4 py-3 font-semibold">Referred</th>
                                                    <th className="px-4 py-3 font-semibold">Payment</th>
                                                    <th className="px-4 py-3 font-semibold">Commission</th>
                                                    <th className="px-4 py-3 font-semibold">Status</th>
                                                    <th className="px-4 py-3 font-semibold w-28">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {commissions.map((c, index) => (
                                                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                                                        <td className="px-4 py-3 text-slate-400">
                                                            {(commissionPage - 1) * REFERRAL_LIST_LIMIT + index + 1}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-800">
                                                            <div>{c.referred_studio_name || c.referred_owner_name || '—'}</div>
                                                            <div className="text-xs text-slate-400">{c.referred_email}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                            {formatMoney(c.payment_amount, c.currency)}
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                                                            {formatMoney(c.commission_amount, c.currency)}
                                                            <span className="text-xs text-slate-400 ml-1">({c.commission_rate}%)</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                                                                c.status === 'paid'
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : 'bg-amber-100 text-amber-700'
                                                            }`}>
                                                                {c.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                            {new Date(c.created_at).toLocaleDateString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalCommissionPages > 1 && (
                                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/80">
                                            <button
                                                type="button"
                                                onClick={() => loadCommissions(commissionPage - 1)}
                                                disabled={loadingCommissions || commissionPage <= 1}
                                                className="text-xs font-bold px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-40"
                                            >
                                                Previous
                                            </button>
                                            <span className="text-xs text-slate-500">
                                                Page {commissionPage} of {totalCommissionPages}
                                                <span className="text-slate-400 ml-1">
                                                    ({commissionRangeStart}–{commissionRangeEnd} of {commissionsTotal})
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => loadCommissions(commissionPage + 1)}
                                                disabled={loadingCommissions || commissionPage >= totalCommissionPages}
                                                className="text-xs font-bold px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-40"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'referrals' && (
                        <div className="animate-fade-in-up">
                            <div className="mb-4">
                                <h3 className="text-base font-bold text-slate-800">Recent referrals</h3>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    {referralsTotal} studio{referralsTotal === 1 ? '' : 's'} joined via your link
                                </p>
                            </div>

                            {loadingReferrals && referrals.length === 0 ? (
                                <p className="py-8 text-center text-sm text-slate-500">Loading referrals...</p>
                            ) : referralsTotal === 0 ? (
                                <div className="py-10 text-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
                                    <Users size={32} className="mx-auto text-slate-300 mb-2" />
                                    <p className="text-sm font-medium text-slate-600">No referrals yet</p>
                                    <p className="text-xs text-slate-400 mt-1">Share your link to get started.</p>
                                </div>
                            ) : (
                                <div className={`rounded-xl border border-slate-200 overflow-hidden ${loadingReferrals ? 'opacity-50' : ''}`}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead>
                                                <tr className="text-slate-500 border-b border-slate-200 bg-slate-50">
                                                    <th className="px-4 py-3 font-semibold w-12">#</th>
                                                    <th className="px-4 py-3 font-semibold">Name</th>
                                                    <th className="px-4 py-3 font-semibold">Email</th>
                                                    <th className="px-4 py-3 font-semibold w-32">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {referrals.map((r, index) => (
                                                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                                                        <td className="px-4 py-3 text-slate-400">
                                                            {(referralPage - 1) * REFERRAL_LIST_LIMIT + index + 1}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-800">
                                                            {r.referred_studio_name || r.referred_owner_name || '—'}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-800">{r.referred_email}</td>
                                                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                            {new Date(r.created_at).toLocaleDateString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalReferralPages > 1 && (
                                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/80">
                                            <button
                                                type="button"
                                                onClick={() => loadReferrals(referralPage - 1)}
                                                disabled={loadingReferrals || referralPage <= 1}
                                                className="text-xs font-bold px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-40"
                                            >
                                                Previous
                                            </button>
                                            <span className="text-xs text-slate-500">
                                                Page {referralPage} of {totalReferralPages}
                                                <span className="text-slate-400 ml-1">
                                                    ({referralRangeStart}–{referralRangeEnd} of {referralsTotal})
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => loadReferrals(referralPage + 1)}
                                                disabled={loadingReferrals || referralPage >= totalReferralPages}
                                                className="text-xs font-bold px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-40"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            )}
        </div>
    );
}
